import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  CreditPurchaseOrderStatus,
  PlatformBillingOrderDetail,
  PlatformBillingOrderRow,
  PlatformCreditPromotionView,
  PlatformTenantBillingView,
  Tenant,
} from '../../api.js';
import { DataTable, type DataColumn } from '../../components/DataTable.js';
import { FormField } from '../../components/FormField.js';
import { TabStrip } from '../../components/TabStrip.js';
import { describeError } from '../../errors.js';
import type { AdminHttpClient } from '../../http.js';
import type { ToastMessage } from '../../types.js';
import {
  canMutateTenantBilling,
  createLatestRequestCoordinator,
  fullUnusedRefundRequest,
  isoToLocalInput,
  parsePartialRefundDraft,
  parsePositiveWon,
  parsePromotionDraft,
  platformBillingOrderRow,
  runMutationWithRefresh,
  type PartialRefundDraft,
  type PromotionDraft,
} from './billing-form.js';

const SECTIONS = [
  { id: 'products', label: 'billingAdmin.productsAndPromotions' },
  { id: 'orders', label: 'billingAdmin.ordersAndRefunds' },
] as const;

const ORDER_STATUSES: CreditPurchaseOrderStatus[] = [
  'created',
  'charging',
  'reconciling',
  'paid',
  'completed',
  'failed',
  'expired',
  'refund_pending',
  'partially_refunded',
  'refunded',
];

const EMPTY_PROMOTION: PromotionDraft = {
  productId: '',
  title: '',
  finalAmountKrw: '',
  startsAt: '',
  endsAt: '',
};

const EMPTY_REFUND: PartialRefundDraft = {
  amountKrw: '',
  revokeCreditMillis: '',
  reason: '',
};

function won(value: number): string {
  return `${value.toLocaleString('ko-KR')}원`;
}

function credits(value: number): string {
  return `${(value / 1_000).toLocaleString('ko-KR')} 크레딧`;
}

export function BillingManagementTab({
  http,
  onToast,
  active,
}: {
  http: AdminHttpClient;
  onToast: (toast: ToastMessage) => void;
  active: boolean;
}) {
  const { t } = useTranslation();
  const [section, setSection] = useState<(typeof SECTIONS)[number]['id']>('products');
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantId, setTenantId] = useState('');
  const [billing, setBilling] = useState<PlatformTenantBillingView>();
  const [priceDrafts, setPriceDrafts] = useState<Record<string, string>>({});
  const [promotionDraft, setPromotionDraft] = useState<PromotionDraft>(EMPTY_PROMOTION);
  const [editingPromotion, setEditingPromotion] = useState<PlatformCreditPromotionView>();
  const [promotionError, setPromotionError] = useState<string>();
  const [tenantListLoading, setTenantListLoading] = useState(false);
  const [tenantLoading, setTenantLoading] = useState(false);
  const [orderLoading, setOrderLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const tenantIdRef = useRef('');
  const tenantRequests = useRef(createLatestRequestCoordinator());

  const [orderTenantId, setOrderTenantId] = useState('');
  const [orderStatus, setOrderStatus] = useState<CreditPurchaseOrderStatus | ''>('');
  const [orderPage, setOrderPage] = useState(1);
  const [orders, setOrders] = useState<PlatformBillingOrderRow[]>([]);
  const [orderTotal, setOrderTotal] = useState(0);
  const [orderError, setOrderError] = useState<unknown>();
  const orderRequests = useRef(createLatestRequestCoordinator());
  const [selectedOrder, setSelectedOrder] = useState<PlatformBillingOrderDetail>();
  const [refundDraft, setRefundDraft] = useState<PartialRefundDraft>(EMPTY_REFUND);
  const [refundError, setRefundError] = useState<string>();
  const loading = tenantListLoading || tenantLoading || orderLoading;

  const fail = useCallback(
    (error: unknown) =>
      onToast({
        tone: 'error',
        message: describeError(error, (key, options) => String(options ? t(key, options) : t(key))),
      }),
    [onToast, t]
  );

  const loadTenant = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id) return false;
      return tenantRequests.current.run({
        request: async () => {
          const next = await http.getTenantBilling(id);
          if (next.tenantId !== id || tenantIdRef.current !== id) {
            throw new Error('Tenant billing response did not match the current selection');
          }
          return next;
        },
        apply: (next) => {
          setBilling(next);
          setEditingPromotion(undefined);
          setPromotionDraft({
            ...EMPTY_PROMOTION,
            productId: next.products[0]?.productId ?? '',
          });
          setPromotionError(undefined);
          setPriceDrafts(
            Object.fromEntries(
              next.products.map((product) => [
                product.productId,
                product.tenantAmountKrw === null ? '' : String(product.tenantAmountKrw),
              ])
            )
          );
        },
        onStart: () => setTenantLoading(true),
        onSettled: () => setTenantLoading(false),
      });
    },
    [http]
  );

  const selectTenant = useCallback((id: string) => {
    tenantIdRef.current = id;
    tenantRequests.current.invalidate();
    setTenantId(id);
    setBilling(undefined);
    setPriceDrafts({});
    setEditingPromotion(undefined);
    setPromotionDraft(EMPTY_PROMOTION);
    setPromotionError(undefined);
    setTenantLoading(Boolean(id));
  }, []);

  const loadOrders = useCallback(() => orderRequests.current.run({
    request: () => http.listBillingOrders({
        ...(orderTenantId ? { tenantId: orderTenantId } : {}),
        ...(orderStatus ? { status: orderStatus } : {}),
        page: orderPage,
        pageSize: 20,
      }),
    apply: (next) => {
      setOrders(next.list);
      setOrderTotal(next.totalCount);
      setOrderPage(next.page);
      setOrderError(undefined);
    },
    onStart: () => setOrderLoading(true),
    onSettled: () => setOrderLoading(false),
  }), [http, orderPage, orderStatus, orderTenantId]);

  // Read-only failures belong to this panel. Keep loadOrders rejecting for
  // refund read-back so a failed refresh cannot produce a success toast.
  const readOrders = useCallback(() => loadOrders().catch((error: unknown) => {
    setOrderError(error);
    setOrders([]);
    setOrderTotal(0);
  }), [loadOrders]);

  const loadOrder = useCallback(
    async (orderId: string) => {
      setOrderLoading(true);
      try {
        setSelectedOrder(await http.getBillingOrder(orderId));
      } finally {
        setOrderLoading(false);
      }
    },
    [http]
  );

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    setTenantListLoading(true);
    void http
      .getTenants()
      .then((next) => {
        if (cancelled) return;
        setTenants(next);
        if (!tenantIdRef.current) selectTenant(next[0]?.id ?? '');
      })
      .catch(fail)
      .finally(() => {
        if (!cancelled) setTenantListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active, fail, http, selectTenant]);

  useEffect(() => {
    if (active && section === 'products') void loadTenant(tenantId).catch(fail);
  }, [active, fail, loadTenant, section, tenantId]);

  useEffect(() => {
    const requests = orderRequests.current;
    if (active && section === 'orders') void readOrders();
    else setOrderLoading(false);
    return () => requests.invalidate();
  }, [active, readOrders, section]);

  async function mutate(
    write: () => Promise<unknown>,
    refresh: () => Promise<unknown>,
    successKey = 'common.saved',
    afterSuccess?: () => void
  ) {
    setBusy(true);
    try {
      await runMutationWithRefresh({
        write,
        refresh,
        onSuccess: () => {
          afterSuccess?.();
          onToast({ tone: 'success', message: t(successKey) });
        },
      });
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  }

  const tenantMutationAllowed = canMutateTenantBilling({
    loading,
    busy,
    tenantId,
    billingTenantId: billing?.tenantId,
  });

  async function refreshTenantAfterMutation(id: string) {
    if (!(await loadTenant(id))) {
      throw new Error('Tenant billing refresh was superseded');
    }
  }

  function savePurchaseEnabled() {
    if (!tenantMutationAllowed || !billing) return;
    const selectedTenantId = tenantId;
    void mutate(
      () =>
        http.setTenantBillingSettings(selectedTenantId, {
          purchaseEnabled: billing.purchaseEnabled,
        }),
      () => refreshTenantAfterMutation(selectedTenantId)
    );
  }

  function savePrice(productId: string) {
    if (
      !tenantMutationAllowed ||
      !billing?.products.some((product) => product.productId === productId)
    ) {
      return;
    }
    const draft = priceDrafts[productId]?.trim() ?? '';
    let amountKrw: number | null = null;
    if (draft) {
      const parsed = parsePositiveWon(draft);
      if (parsed === undefined) {
        onToast({ tone: 'error', message: t('billingAdmin.priceInvalid') });
        return;
      }
      amountKrw = parsed;
    }
    const selectedTenantId = tenantId;
    void mutate(
      () => http.setTenantCreditPrice(selectedTenantId, productId, { amountKrw }),
      () => refreshTenantAfterMutation(selectedTenantId)
    );
  }

  function editPromotion(promotion: PlatformCreditPromotionView) {
    if (
      !tenantMutationAllowed ||
      promotion.tenantId !== tenantId ||
      !billing?.promotions.some((candidate) => candidate.id === promotion.id)
    ) {
      return;
    }
    setEditingPromotion(promotion);
    setPromotionDraft({
      productId: promotion.productId,
      title: promotion.title,
      finalAmountKrw: String(promotion.finalAmountKrw),
      startsAt: isoToLocalInput(promotion.startsAt),
      endsAt: isoToLocalInput(promotion.endsAt),
    });
    setPromotionError(undefined);
  }

  function clearPromotion() {
    setEditingPromotion(undefined);
    setPromotionDraft({
      ...EMPTY_PROMOTION,
      productId: billing?.products[0]?.productId ?? '',
    });
    setPromotionError(undefined);
  }

  function savePromotion(event: FormEvent) {
    event.preventDefault();
    if (!tenantMutationAllowed || !billing) return;
    const parsed = parsePromotionDraft(promotionDraft);
    if (!parsed.ok) {
      setPromotionError(t(`billingAdmin.promotionInvalid.${parsed.field}`));
      return;
    }
    if (!billing.products.some((product) => product.productId === parsed.value.productId)) {
      setPromotionError(t('billingAdmin.promotionInvalid.productId'));
      return;
    }
    if (
      editingPromotion &&
      (editingPromotion.tenantId !== tenantId ||
        !billing.promotions.some((promotion) => promotion.id === editingPromotion.id))
    ) {
      return;
    }
    setPromotionError(undefined);
    const selectedTenantId = tenantId;
    const promotion = editingPromotion;
    void mutate(
      () =>
        promotion
          ? http.updateCreditPromotion(selectedTenantId, promotion.id, parsed.value)
          : http.createCreditPromotion(selectedTenantId, parsed.value),
      () => refreshTenantAfterMutation(selectedTenantId),
      'billingAdmin.promotionSaved',
      clearPromotion
    );
  }

  function deletePromotion(promotion: PlatformCreditPromotionView) {
    if (
      !tenantMutationAllowed ||
      promotion.tenantId !== tenantId ||
      !billing?.promotions.some((candidate) => candidate.id === promotion.id)
    ) {
      return;
    }
    if (!window.confirm(t('billingAdmin.deletePromotionConfirm', { title: promotion.title }))) {
      return;
    }
    const selectedTenantId = tenantId;
    void mutate(
      () => http.deleteCreditPromotion(selectedTenantId, promotion.id),
      () => refreshTenantAfterMutation(selectedTenantId),
      'billingAdmin.promotionDeleted',
      editingPromotion?.id === promotion.id ? clearPromotion : undefined
    );
  }

  async function refreshOrderAfterRefund(orderId: string) {
    const [, refreshed] = await Promise.all([loadOrder(orderId), loadOrders()]);
    if (!refreshed) throw new Error('Order list refresh was superseded');
  }

  function refundFull() {
    if (!selectedOrder || !window.confirm(t('billingAdmin.fullRefundConfirm'))) return;
    const orderId = selectedOrder.orderId;
    void mutate(
      () =>
        http.refundBillingOrder(
          orderId,
          fullUnusedRefundRequest(t('billingAdmin.fullRefundReason'))
        ),
      () => refreshOrderAfterRefund(orderId),
      'billingAdmin.refundCompleted'
    );
  }

  function refundPartial(event: FormEvent) {
    event.preventDefault();
    if (!selectedOrder) return;
    const parsed = parsePartialRefundDraft(refundDraft, selectedOrder);
    if (!parsed.ok) {
      setRefundError(t(`billingAdmin.refundInvalid.${parsed.field}`));
      return;
    }
    if (!window.confirm(t('billingAdmin.partialRefundConfirm'))) return;
    const orderId = selectedOrder.orderId;
    setRefundError(undefined);
    void mutate(
      () => http.refundBillingOrder(orderId, parsed.value),
      () => refreshOrderAfterRefund(orderId),
      'billingAdmin.refundCompleted',
      () => setRefundDraft(EMPTY_REFUND)
    );
  }

  const orderColumns = useMemo<DataColumn<PlatformBillingOrderRow>[]>(
    () => [
      { key: 'order', header: t('billingAdmin.orderId'), render: (row) => row.orderId },
      { key: 'tenant', header: t('common.tenant'), render: (row) => row.tenantId },
      { key: 'user', header: t('common.user'), render: (row) => row.redactedUserId },
      { key: 'status', header: t('common.status'), render: (row) => row.status },
      {
        key: 'amount',
        header: t('billingAdmin.amount'),
        render: (row) => platformBillingOrderRow(row).amount,
      },
      {
        key: 'credits',
        header: t('billingAdmin.credits'),
        render: (row) => platformBillingOrderRow(row).credits,
      },
      {
        key: 'created',
        header: t('billingAdmin.createdAt'),
        render: (row) => platformBillingOrderRow(row).createdAt,
      },
      {
        key: 'actions',
        header: t('common.actions'),
        render: (row) => (
          <button
            type="button"
            className="ghost"
            disabled={loading}
            onClick={() => void loadOrder(row.orderId).catch(fail)}
          >
            {t('billingAdmin.details')}
          </button>
        ),
      },
    ],
    [fail, loadOrder, loading, t]
  );

  return (
    <section className="content-stack billing-admin" aria-busy={loading || busy}>
      <TabStrip
        tabs={SECTIONS.map((item) => ({ id: item.id, label: t(item.label) }))}
        value={section}
        onChange={setSection}
        level="nested"
      />
      {loading ? (
        <p className="muted" role="status">
          {t('billingAdmin.loading')}
        </p>
      ) : null}

      <div hidden={section !== 'products'} className={section === 'products' ? 'content-stack' : undefined}>
        <section className="panel billing-tenant-selector">
          <label>
            {t('tenantScope.billingTarget')}
            <select
              value={tenantId}
              onChange={(event) => selectTenant(event.target.value)}
              disabled={busy}
            >
              <option value="">{t('tenantScope.selectTarget')}</option>
              {tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name} ({tenant.id})
                </option>
              ))}
            </select>
          </label>
        </section>

        {billing?.tenantId === tenantId ? (
          <>
            <section className="panel billing-setting-row">
              <label className="check-row">
                <input
                  type="checkbox"
                  checked={billing.purchaseEnabled}
                  disabled={!tenantMutationAllowed}
                  onChange={(event) =>
                    setBilling({ ...billing, purchaseEnabled: event.target.checked })
                  }
                />
                <span>{t('billingAdmin.purchaseEnabled')}</span>
              </label>
              <button type="button" disabled={!tenantMutationAllowed} onClick={savePurchaseEnabled}>
                {t('common.save')}
              </button>
            </section>

            <section className="panel content-stack">
              <h2>{t('billingAdmin.products')}</h2>
              <div className="billing-product-grid">
                {billing.products.map((product) => {
                  const offer = billing.effectiveOffers.find(
                    (item) => item.productId === product.productId
                  );
                  return (
                    <article className="billing-product-card" key={product.productId}>
                      <strong>{product.sku}</strong>
                      <small>
                        {t('billingAdmin.defaultPrice')}: {won(product.defaultAmountKrw)}
                      </small>
                      <FormField
                        label={t('billingAdmin.tenantPrice')}
                        inputMode="numeric"
                        placeholder={t('billingAdmin.restoreDefault')}
                        value={priceDrafts[product.productId] ?? ''}
                        disabled={!tenantMutationAllowed}
                        onChange={(event) =>
                          setPriceDrafts((current) => ({
                            ...current,
                            [product.productId]: event.target.value,
                          }))
                        }
                      />
                      <button
                        type="button"
                        disabled={!tenantMutationAllowed}
                        onClick={() => savePrice(product.productId)}
                      >
                        {t('common.save')}
                      </button>
                      <p className="billing-effective-offer">
                        <span>{t('billingAdmin.effectivePrice')}</span>
                        <strong>
                          {won(
                            offer?.finalAmountKrw ??
                              product.tenantAmountKrw ??
                              product.defaultAmountKrw
                          )}
                        </strong>
                        {offer?.promotionEndsAt ? (
                          <small>
                            {t('billingAdmin.discountUntil', {
                              at: new Date(offer.promotionEndsAt).toLocaleString(),
                            })}
                          </small>
                        ) : null}
                      </p>
                    </article>
                  );
                })}
              </div>
            </section>

            <section className="billing-promotion-layout">
              <form className="panel form-grid" onSubmit={savePromotion}>
                <h2>
                  {editingPromotion
                    ? t('billingAdmin.updatePromotion')
                    : t('billingAdmin.createPromotion')}
                </h2>
                <label>
                  {t('billingAdmin.product')}
                  <select
                    value={promotionDraft.productId}
                    disabled={!tenantMutationAllowed}
                    onChange={(event) =>
                      setPromotionDraft({ ...promotionDraft, productId: event.target.value })
                    }
                  >
                    <option value="">{t('billingAdmin.selectProduct')}</option>
                    {billing.products.map((product) => (
                      <option key={product.productId} value={product.productId}>
                        {product.sku}
                      </option>
                    ))}
                  </select>
                </label>
                <FormField
                  label={t('billingAdmin.promotionTitle')}
                  value={promotionDraft.title}
                  disabled={!tenantMutationAllowed}
                  onChange={(event) =>
                    setPromotionDraft({ ...promotionDraft, title: event.target.value })
                  }
                />
                <FormField
                  label={t('billingAdmin.finalAmount')}
                  inputMode="numeric"
                  value={promotionDraft.finalAmountKrw}
                  disabled={!tenantMutationAllowed}
                  onChange={(event) =>
                    setPromotionDraft({ ...promotionDraft, finalAmountKrw: event.target.value })
                  }
                />
                <FormField
                  label={t('billingAdmin.startsAt')}
                  type="datetime-local"
                  value={promotionDraft.startsAt}
                  disabled={!tenantMutationAllowed}
                  onChange={(event) =>
                    setPromotionDraft({ ...promotionDraft, startsAt: event.target.value })
                  }
                />
                <FormField
                  label={t('billingAdmin.endsAt')}
                  type="datetime-local"
                  value={promotionDraft.endsAt}
                  disabled={!tenantMutationAllowed}
                  onChange={(event) =>
                    setPromotionDraft({ ...promotionDraft, endsAt: event.target.value })
                  }
                />
                {promotionError ? (
                  <p className="error-text" role="alert">
                    {promotionError}
                  </p>
                ) : null}
                <div className="form-actions">
                  <button disabled={!tenantMutationAllowed}>
                    {editingPromotion
                      ? t('billingAdmin.updatePromotion')
                      : t('billingAdmin.createPromotion')}
                  </button>
                  {editingPromotion ? (
                    <button
                      type="button"
                      className="ghost"
                      disabled={busy}
                      onClick={clearPromotion}
                    >
                      {t('common.cancel')}
                    </button>
                  ) : null}
                </div>
              </form>

              <section className="panel content-stack">
                <h2>{t('billingAdmin.promotions')}</h2>
                {billing.promotions.length ? (
                  <ul className="plain-list billing-promotion-list">
                    {billing.promotions.map((promotion) => (
                      <li key={promotion.id}>
                        <span>
                          <strong>{promotion.title || promotion.productId}</strong>
                          <small>
                            {won(promotion.finalAmountKrw)} ·{' '}
                            {new Date(promotion.startsAt).toLocaleString()} –{' '}
                            {new Date(promotion.endsAt).toLocaleString()}
                          </small>
                        </span>
                        <span className="form-actions">
                          <button
                            type="button"
                            className="ghost"
                            disabled={!tenantMutationAllowed || promotion.tenantId !== tenantId}
                            onClick={() => editPromotion(promotion)}
                          >
                            {t('common.edit')}
                          </button>
                          <button
                            type="button"
                            className="danger"
                            disabled={!tenantMutationAllowed || promotion.tenantId !== tenantId}
                            onClick={() => deletePromotion(promotion)}
                          >
                            {t('common.delete')}
                          </button>
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="muted">{t('billingAdmin.noPromotions')}</p>
                )}
              </section>
            </section>
          </>
        ) : tenantId ? null : (
          <p className="panel muted">{t('billingAdmin.selectTenant')}</p>
        )}
      </div>

      <div hidden={section !== 'orders'} className={section === 'orders' ? 'content-stack' : undefined}>
        <section className="panel toolbar-form billing-order-filters">
          <label>
            {t('tenantScope.orderFilter')}
            <select
              value={orderTenantId}
              onChange={(event) => {
                setOrderTenantId(event.target.value);
                setOrderPage(1);
              }}
            >
              <option value="">{t('tenantScope.allTenants')}</option>
              {tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name} ({tenant.id})
                </option>
              ))}
            </select>
          </label>
          <label>
            {t('common.status')}
            <select
              value={orderStatus}
              onChange={(event) => {
                setOrderStatus(event.target.value as CreditPurchaseOrderStatus | '');
                setOrderPage(1);
              }}
            >
              <option value="">{t('billingAdmin.allStatuses')}</option>
              {ORDER_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="ghost"
            disabled={loading}
            onClick={() => void readOrders()}
          >
            {t('common.refresh')}
          </button>
        </section>
        <section className="panel">
          {orderError !== undefined ? (
            <div className="billing-order-error" role="alert">
              <h2>{t('billingAdmin.orderLoadFailed')}</h2>
              <p>{describeError(orderError, t)}</p>
              <button type="button" disabled={orderLoading} onClick={() => void readOrders()}>
                {t(orderLoading ? 'common.loading' : 'billingAdmin.retryOrders')}
              </button>
            </div>
          ) : null}
          {orderLoading ? <p role="status">{t('common.loading')}</p> : null}
          <DataTable
            rows={orders}
            columns={orderColumns}
            empty={t(orderLoading ? 'common.loading' : orderError !== undefined ? 'billingAdmin.orderListUnavailable' : 'billingAdmin.noOrders')}
            pageSize={20}
            getRowKey={(row) => row.orderId}
            server={{
              total: orderTotal,
              page: orderPage - 1,
              onPageChange: (page) => setOrderPage(page + 1),
            }}
            mobileCards
          />
        </section>

        {selectedOrder ? (
          <section className="billing-order-detail">
            <section className="panel content-stack">
              <h2>{t('billingAdmin.details')}</h2>
              <dl className="billing-order-summary">
                <div>
                  <dt>{t('billingAdmin.orderId')}</dt>
                  <dd>{selectedOrder.orderId}</dd>
                </div>
                <div>
                  <dt>{t('common.tenant')}</dt>
                  <dd>{selectedOrder.tenantId}</dd>
                </div>
                <div>
                  <dt>{t('common.user')}</dt>
                  <dd>{selectedOrder.redactedUserId}</dd>
                </div>
                <div>
                  <dt>{t('common.status')}</dt>
                  <dd>{selectedOrder.status}</dd>
                </div>
                <div>
                  <dt>{t('billingAdmin.amount')}</dt>
                  <dd>{won(selectedOrder.finalAmountKrw)}</dd>
                </div>
                <div>
                  <dt>{t('billingAdmin.credits')}</dt>
                  <dd>{credits(selectedOrder.creditMillis)}</dd>
                </div>
                <div>
                  <dt>{t('billingAdmin.cancelableAmount')}</dt>
                  <dd>{won(selectedOrder.cancelableAmountKrw)}</dd>
                </div>
                <div>
                  <dt>{t('billingAdmin.unusedCredits')}</dt>
                  <dd>{credits(selectedOrder.unusedCreditMillis)}</dd>
                </div>
              </dl>
              <h3>{t('billingAdmin.timeline')}</h3>
              {selectedOrder.events.length ? (
                <ol className="event-list">
                  {[...selectedOrder.events]
                    .sort((a, b) => a.at.localeCompare(b.at))
                    .map((event) => (
                      <li className="event-row" key={event.id}>
                        <span>
                          <strong>{event.type}</strong>
                          {event.code ? <small>{event.code}</small> : null}
                        </span>
                        <time dateTime={event.at}>{new Date(event.at).toLocaleString()}</time>
                      </li>
                    ))}
                </ol>
              ) : (
                <p className="muted">{t('billingAdmin.noEvents')}</p>
              )}
            </section>

            <section className="panel content-stack">
              <h2>{t('billingAdmin.refund')}</h2>
              <button
                type="button"
                disabled={
                  busy ||
                  selectedOrder.cancelableAmountKrw !== selectedOrder.finalAmountKrw ||
                  selectedOrder.unusedCreditMillis !== selectedOrder.creditMillis
                }
                onClick={refundFull}
              >
                {t('billingAdmin.fullUnusedRefund')}
              </button>
              <form className="form-grid" onSubmit={refundPartial}>
                <h3>{t('billingAdmin.partialRefund')}</h3>
                <FormField
                  label={t('billingAdmin.refundAmount')}
                  inputMode="numeric"
                  value={refundDraft.amountKrw}
                  disabled={busy || !selectedOrder.isPartialCancelable}
                  onChange={(event) =>
                    setRefundDraft({ ...refundDraft, amountKrw: event.target.value })
                  }
                />
                <FormField
                  label={t('billingAdmin.revokeCreditsMillis')}
                  inputMode="numeric"
                  value={refundDraft.revokeCreditMillis}
                  disabled={busy || !selectedOrder.isPartialCancelable}
                  onChange={(event) =>
                    setRefundDraft({ ...refundDraft, revokeCreditMillis: event.target.value })
                  }
                />
                <FormField
                  label={t('billingAdmin.reason')}
                  value={refundDraft.reason}
                  disabled={busy || !selectedOrder.isPartialCancelable}
                  onChange={(event) =>
                    setRefundDraft({ ...refundDraft, reason: event.target.value })
                  }
                />
                {refundError ? (
                  <p className="error-text" role="alert">
                    {refundError}
                  </p>
                ) : null}
                <button disabled={busy || !selectedOrder.isPartialCancelable}>
                  {t('billingAdmin.partialRefund')}
                </button>
              </form>
            </section>
          </section>
        ) : null}
      </div>
    </section>
  );
}
